// Landing after scanning a printed QR (screens 1-2 of the flow: form + permissions).
// Route shape is frozen: /#/user-scan-qrcode/:token/:detailsToken (backend redirect).
import { useEffect, useMemo, useRef, useState } from 'react'
import BrandLockup from '../components/BrandLockup'
import LanguageSwitch from '../components/LanguageSwitch'
import { AlertIcon, CameraIcon, ClockIcon, MicIcon, OfflineIcon, PhoneIcon, StatusIcon } from '../components/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import { fetchIceConfig } from '../core/iceConfig'
import { currentLanguage, t } from '../i18n'
import { offersAssistant, readAssistantMode } from '../assistant'
import { embeddedInKiosk, returnedFromAssistant } from '../launchContext'
import { useAssistantLauncher } from '../components/useAssistantLauncher'

// 'choice': THE QR OFFERS THE VIRTUAL ASSISTANT OR A CALL TO RECEPTION BEFORE ANYTHING ELSE.
type Step = 'choice' | 'form' | 'permissions'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PHONE_RE = /^\+?[0-9 ()-]{6,18}$/

const decodeSafe = (token: string): any => {
  try {
    return jwtDecode(token)
  } catch {
    return null
  }
}

// A CROSS-ORIGIN PARENT CAN MAKE READING window.top THROW; TREAT THAT AS EMBEDDED.
const isEmbedded = (): boolean => {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

// FIELD DEFINITIONS COME FROM THE QR TOKEN AS JSON STRINGS: {"<label>":"<type>"}
const parseFields = (fields: any): { key: string; type: string }[] => {
  if (!Array.isArray(fields)) return []
  return fields
    .map((f: string) => {
      try {
        const obj = JSON.parse(f)
        const key = Object.keys(obj)[0]
        return { key, type: String(obj[key]) }
      } catch {
        return null
      }
    })
    .filter(Boolean) as { key: string; type: string }[]
}

const validateField = (type: string, value: string): string | null => {
  if (!value || !value.trim()) return t.fieldRequired
  if (type === 'email' && !EMAIL_RE.test(value.trim())) return t.invalidEmail
  if (type === 'contact_no' && !PHONE_RE.test(value.trim())) return t.invalidPhone
  return null
}

const ScanLanding = () => {
  const navigate = useNavigate()
  const { token, detailsToken } = useParams()

  const isExpiredRedirect = token === 'qrcode_expired'
  // ON THE EXPIRED PAGE THE SECOND SEGMENT SAYS WHY: expired (THE CODE), link_expired OR
  // link_used (A SHORT-LIVED LINK SHOWN ON A SCREEN).
  const expiredReason = isExpiredRedirect ? detailsToken : undefined
  const tokenDecoded = useMemo(() => (isExpiredRedirect ? null : decodeSafe(token || '')), [token])
  const detailDecoded = useMemo(() => (detailsToken === 'expired' ? null : decodeSafe(detailsToken || '')), [detailsToken])

  const enabledDirectCall = tokenDecoded?.enabledDirectCall == 1
  const fields = useMemo(
    () => (enabledDirectCall ? [] : parseFields(tokenDecoded?.fields)),
    [tokenDecoded, enabledDirectCall]
  )

  const offerChoice = offersAssistant('landing', {
    mode: readAssistantMode(tokenDecoded),
    returnedFromAssistant: returnedFromAssistant(),
    embeddedInKiosk,
  })
  const assistant = useAssistantLauncher(token && detailsToken ? { token, details: detailsToken } : null, offerChoice)

  const [isLoaded, setIsLoaded] = useState(false)
  const [webrtcConfig, setWebrtcConfig] = useState<any>(undefined)
  const [step, setStep] = useState<Step>(offerChoice ? 'choice' : 'form')
  const [values, setValues] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [permissionHelp, setPermissionHelp] = useState('')
  const [requesting, setRequesting] = useState(false)
  const [checkingPerms, setCheckingPerms] = useState(false)
  const autoRequested = useRef(false)

  useEffect(() => {
    // REMEMBER THE ENTRY URL SO END/ERROR SCREENS CAN OFFER "LLAMAR DE NUEVO".
    if (!isExpiredRedirect && tokenDecoded) {
      sessionStorage.setItem('cf_scan_url', window.location.hash)
    }
    fetchIceConfig().then((config) => {
      setWebrtcConfig(config || undefined)
      setIsLoaded(true)
    })
  }, [])

  // DIRECT-CALL QRS SKIP THE FORM AND GO STRAIGHT TO THE PERMISSION STEP (AFTER THE
  // ASSISTANT CHOICE, WHEN THE QR OFFERS ONE).
  useEffect(() => {
    if (isLoaded && enabledDirectCall) {
      setStep((current) => (current === 'form' ? 'permissions' : current))
    }
  }, [isLoaded, enabledDirectCall])

  const callReception = () => setStep(enabledDirectCall ? 'permissions' : 'form')

  // IF THE BROWSER ALREADY GRANTED CAMERA+MICROPHONE, SKIP THE PERMISSION SCREEN AND CALL
  // DIRECTLY (SAME AS THE PREVIOUS CLIENT: granted -> startCall WITH NO EXTRA TAP).
  // A DIRECT-CALL QR OPENED INSIDE A FRAME IS THE KIOSK'S ASSISTANCE CALL: THE HOST GRANTS
  // MEDIA WITHOUT A PROMPT, BUT ONLY WHEN SOMETHING ASKS, SO THE QUERY STILL SAYS 'prompt'
  // ON A FRESH PROFILE. ASK ONCE WITHOUT THE EXPLANATION SCREEN; IF THAT FAILS, THE SCREEN
  // SHOWS WITH ITS HELP MESSAGE. OUTSIDE A FRAME THE GUEST STILL SEES THE EXPLANATION FIRST.
  useEffect(() => {
    if (step !== 'permissions') return
    const autoRequest = enabledDirectCall && isEmbedded() && !autoRequested.current
    if (!navigator.permissions?.query && !autoRequest) return
    let cancelled = false
    setCheckingPerms(true)
    const alreadyGranted = navigator.permissions?.query
      ? Promise.all([
          navigator.permissions.query({ name: 'camera' as PermissionName }),
          navigator.permissions.query({ name: 'microphone' as PermissionName }),
        ]).then(([cam, mic]) => cam.state === 'granted' && mic.state === 'granted')
      : Promise.resolve(false)
    alreadyGranted
      .catch(() => false)
      .then(async (granted) => {
        if (cancelled) return
        if (granted) {
          startCall(values)
          return
        }
        if (autoRequest) {
          autoRequested.current = true
          if ((await requestPermissions()) || cancelled) return
        }
        setCheckingPerms(false)
      })
    return () => {
      cancelled = true
    }
  }, [step])

  const startCall = (forminput: Record<string, string>) => {
    const qrCodeData = {
      forminput: forminput,
      qrOrgName: detailDecoded?.qrOrgName,
      qrcodeName: detailDecoded?.qrcodeName,
      defaultstaff: detailDecoded?.defaultstaff,
      lastcalled: detailDecoded?.lastcalled,
      door_id: tokenDecoded?.door_id,
      enabledDirectCall: tokenDecoded?.enabledDirectCall,
      lastattendedby: detailDecoded?.lastattendedby,
    }
    const state = {
      staffToCall: detailDecoded?.staffsArr,
      qrCodeId: detailDecoded?.qrcodeId,
      orgId: tokenDecoded?.organizationId,
      door_id: tokenDecoded?.door_id,
      enabledDirectCall: tokenDecoded?.enabledDirectCall,
      // Per-QR setting deciding whether the callback contact form is offered when no
      // agent is available (#60). May be absent on a scan token issued before the
      // backend field existed; VideoState falls back to the kiosk inference then.
      showCallbackForm: tokenDecoded?.showCallbackForm,
      // THE SERVER CHECKS IT BEFORE RINGING ANYBODY: IT NAMES THE CODE, AND A SHORT-LIVED LINK'S
      // TOKEN EXPIRES. THE LANGUAGE PICKS THE MESSAGE SHOWN IF THE CALL IS REFUSED.
      callToken: detailsToken,
      lang: currentLanguage,
      userInfo: qrCodeData,
      webrtcConfig: webrtcConfig,
      callingStatus: 'O',
    }
    navigate('/calling', { state })
  }

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const nextErrors: Record<string, string> = {}
    fields.forEach((f) => {
      const err = validateField(f.type, values[f.key] || '')
      if (err) nextErrors[f.key] = err
    })
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length === 0) {
      setStep('permissions')
    }
  }

  // EXPLICIT PRE-PERMISSION STEP BEFORE getUserMedia. THE OLD FLOW ASKED FOR CAMERA AND
  // MICROPHONE TOGETHER, SO WE KEEP THAT; IF THE CAMERA IS UNAVAILABLE ON A NON-DIRECT
  // CALL WE FALL BACK TO MICROPHONE ONLY (THE CALL STARTS AS AUDIO ANYWAY).
  const requestPermissions = async (): Promise<boolean> => {
    setRequesting(true)
    setPermissionHelp('')
    const videoConstraints = {
      width: { min: 300, max: 640 },
      height: { min: 200, max: 480 },
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: true })
      stream.getTracks().forEach((t) => t.stop())
      startCall(values)
      return true
    } catch (err: any) {
      if (!enabledDirectCall) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
          stream.getTracks().forEach((t) => t.stop())
          startCall(values)
          return true
        } catch {
          /* FALLS THROUGH TO THE HELP MESSAGE BELOW */
        }
      }
      setPermissionHelp(t.permissionsDenied)
      return false
    } finally {
      setRequesting(false)
    }
  }

  // ---------- ERROR VARIANTS ----------

  if (isExpiredRedirect || (!tokenDecoded && isLoaded)) {
    return (
      <div className="cf-page">
        <div className="cf-card">
          <BrandLockup />
          <StatusIcon tone="warning" icon={ClockIcon} />
          <h1 className="cf-title">
            {expiredReason === 'link_used' ? t.linkUsedTitle : expiredReason === 'link_expired' ? t.linkExpiredTitle : t.qrExpiredTitle}
          </h1>
          <p className="cf-subtitle">
            {expiredReason === 'link_used' ? t.linkUsedIntro : expiredReason === 'link_expired' ? t.linkExpiredIntro : t.qrExpiredIntro}
          </p>
        </div>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div className="cf-page">
        <div className="cf-card">
          <BrandLockup />
          <p className="cf-subtitle">{t.loading}</p>
        </div>
      </div>
    )
  }

  if (typeof webrtcConfig === 'undefined') {
    return (
      <div className="cf-page">
        <div className="cf-card">
          <BrandLockup />
          <StatusIcon tone="warning" icon={OfflineIcon} />
          <h1 className="cf-title">{t.serviceUnavailableTitle}</h1>
          <p className="cf-subtitle">{t.serviceUnavailablePrepare}</p>
          <button className="cf-btn cf-btn-primary" onClick={() => window.location.reload()}>
            {t.retry}
          </button>
        </div>
      </div>
    )
  }

  // QR SCANNED BEFORE ITS AVAILABILITY TIME.
  if (tokenDecoded && Math.round(Date.now()) < tokenDecoded.iat) {
    const availableFrom = new Intl.DateTimeFormat('en-GB', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(tokenDecoded.iat))
    return (
      <div className="cf-page">
        <div className="cf-card">
          <BrandLockup />
          <StatusIcon tone="warning" icon={ClockIcon} />
          <h1 className="cf-title">{t.notYetAvailableTitle}</h1>
          <p className="cf-subtitle">{t.availableFrom(availableFrom)}</p>
        </div>
      </div>
    )
  }

  if (!enabledDirectCall && detailDecoded?.qrcodeStatus !== 'active') {
    return (
      <div className="cf-page">
        <div className="cf-card">
          <BrandLockup />
          <StatusIcon tone="warning" icon={AlertIcon} />
          <h1 className="cf-title">{t.serviceUnavailableTitle}</h1>
          <p className="cf-subtitle">{t.serviceInactive}</p>
        </div>
      </div>
    )
  }

  // ---------- SCREEN 0: VIRTUAL ASSISTANT OR RECEPTION ----------

  if (step === 'choice') {
    return (
      <div className="cf-page">
        <div className="cf-card">
          <BrandLockup />
          <h1 className="cf-title">{detailDecoded?.qrOrgName || t.landingDefaultTitle}</h1>
          {detailDecoded?.qrcodeName && <p className="cf-subtitle">{detailDecoded.qrcodeName}</p>}
          <p className="cf-subtitle">{t.assistantChoiceIntro}</p>
          {(assistant.failed || assistant.unavailable) && (
            <div className="cf-alert-danger" role="alert">
              {t.assistantUnavailable}
            </div>
          )}
          <button className="cf-btn cf-btn-primary" onClick={assistant.open} disabled={assistant.opening || assistant.unavailable}>
            <MicIcon />
            {assistant.opening ? t.assistantOpening : t.assistantTalk}
          </button>
          {/* WITH THE ASSISTANT UNAVAILABLE, RECEPTION IS THE WAY FORWARD. */}
          <button className={`cf-btn ${assistant.unavailable ? 'cf-btn-primary' : 'cf-btn-secondary'}`} onClick={callReception} disabled={assistant.opening}>
            <PhoneIcon />
            {t.assistantCallReception}
          </button>
          <p className="cf-note">{t.assistantVoiceNote}</p>
          <LanguageSwitch />
        </div>
      </div>
    )
  }

  // ---------- SCREEN 2: PERMISSIONS ----------

  if (step === 'permissions') {
    if (checkingPerms) {
      return (
        <div className="cf-page">
          <div className="cf-card">
            <BrandLockup />
            <p className="cf-subtitle">{t.preparingCall}</p>
          </div>
        </div>
      )
    }
    return (
      <div className="cf-page">
        <div className="cf-card">
          <BrandLockup />
          <div className="cf-permission-icons">
            <div className="cf-status-icon primary">
              <MicIcon />
            </div>
            <div className="cf-status-icon primary">
              <CameraIcon />
            </div>
          </div>
          <h1 className="cf-title">{t.permissionsTitle}</h1>
          <p className="cf-subtitle">{t.permissionsIntro}</p>
          {permissionHelp && <div className="cf-help">{permissionHelp}</div>}
          <button className="cf-btn cf-btn-primary" onClick={requestPermissions} disabled={requesting}>
            {requesting ? t.requestingPermissions : t.allowAndContinue}
          </button>
          {!enabledDirectCall && (
            <button className="cf-btn cf-btn-secondary" onClick={() => setStep('form')}>
              {t.back}
            </button>
          )}
        </div>
      </div>
    )
  }

  // ---------- SCREEN 1: LANDING + CALLER FORM ----------

  return (
    <div className="cf-page">
      <div className="cf-card">
        <BrandLockup />
        <h1 className="cf-title">{detailDecoded?.qrOrgName || t.landingDefaultTitle}</h1>
        {detailDecoded?.qrcodeName && <p className="cf-subtitle">{detailDecoded.qrcodeName}</p>}
        <p className="cf-subtitle">{t.landingIntro}</p>

        <form onSubmit={handleFormSubmit} autoComplete="off" noValidate>
          {fields.map((f) => (
            <div className="cf-field" key={f.key}>
              <label htmlFor={`cf-${f.key}`}>{f.key}</label>
              <input
                id={`cf-${f.key}`}
                name={f.key}
                type={f.type === 'email' ? 'email' : f.type === 'contact_no' ? 'tel' : 'text'}
                inputMode={f.type === 'contact_no' ? 'tel' : undefined}
                value={values[f.key] || ''}
                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
              />
              {errors[f.key] && <span className="cf-error">{errors[f.key]}</span>}
            </div>
          ))}

          <button type="submit" className="cf-btn cf-btn-primary">
            <PhoneIcon />
            {t.startCall}
          </button>
        </form>

        <LanguageSwitch />
      </div>
    </div>
  )
}

export default ScanLanding
